<script setup lang="ts">
import { ref } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { AppAlert, AppButton, FormInput } from 'duanaga-ui'
import { z } from 'zod'
import FetchError from '@/FetchError'
import Auth from '@/api/Auth'
import { signInWithAppleIdentity } from '@/composables/social/appleIdentity'
import { signInWithGoogleIdentity } from '@/composables/social/googleIdentity'
import useTitle from '@/composables/title'
import { useValidator } from '@/composables/validator'
import { useAuthStore } from '@/stores/auth'

useTitle('Login')

const router = useRouter()
const auth = useAuthStore()
const adminDashboardUrl = import.meta.env.VITE_ADMIN_DASHBOARD_URL?.trim()

const authenticating = ref(false)
const socialAuthenticating = ref<SocialProvider | null>(null)
const clientError = ref<string | null>(null)

const featureHighlights = [
  {
    title: 'Authenticity guaranteed',
    description: 'Every generated code is cryptographically signed and verified in real-time.'
  },
  {
    title: 'Clinical precision',
    description: 'Monitor scans, track suspicious activity, and prevent counterfeits.'
  },
  {
    title: 'Secure orchestrator',
    description: 'Manage batches and printer routing from one fortified, elegant workspace.'
  }
]

const schema = z.object({
  email: z.string().email('Valid email is required'),
  password: z.string().min(1, 'Password is required')
})

const {
  formData: credentials,
  formError,
  validate
} = useValidator(schema, {
  email: '',
  password: ''
})

const completeLogin = async (response: AuthResponse): Promise<void> => {
  auth.authenticate({
    token: response.token,
    user: response.user
  })

  if (response.user.role === 'admin' && adminDashboardUrl) {
    const redirectUrl = new URL(adminDashboardUrl)
    redirectUrl.searchParams.set('token', response.token)
    redirectUrl.searchParams.set('role', response.user.role)
    redirectUrl.searchParams.set('email', response.user.email)
    redirectUrl.searchParams.set('name', response.user.name)
    window.location.assign(redirectUrl.toString())
    return
  }

  await router.push({ name: 'batches' })
}

const toMessage = (error: unknown, fallback: string): string => {
  if (error instanceof FetchError) {
    return error.message || fallback
  }

  if (error instanceof Error) {
    return error.message || fallback
  }

  return fallback
}

const attemptLogin = async (): Promise<void> => {
  if (!validate()) {
    return
  }

  authenticating.value = true
  clientError.value = null

  try {
    const response = await Auth.authenticate<AuthResponse>({
      email: credentials.value.email,
      password: credentials.value.password
    })

    await completeLogin(response)
  } catch (error) {
    clientError.value = toMessage(error, 'Unable to sign in. Please try again.')
  } finally {
    authenticating.value = false
  }
}

const attemptGoogleLogin = async (): Promise<void> => {
  socialAuthenticating.value = 'google'
  clientError.value = null

  try {
    const identity = await signInWithGoogleIdentity()
    const response = await Auth.authenticateSocial<SocialAuthResponse>({
      provider: 'google',
      id_token: identity.idToken,
      device_name: 'web-client'
    })

    await completeLogin(response)
  } catch (error) {
    clientError.value = toMessage(error, 'Unable to sign in with Google.')
  } finally {
    socialAuthenticating.value = null
  }
}

const attemptAppleLogin = async (): Promise<void> => {
  socialAuthenticating.value = 'apple'
  clientError.value = null

  try {
    const identity = await signInWithAppleIdentity()
    const response = await Auth.authenticateSocial<SocialAuthResponse>({
      provider: 'apple',
      id_token: identity.idToken,
      nonce: identity.nonce,
      name: identity.name,
      device_name: 'web-client'
    })

    await completeLogin(response)
  } catch (error) {
    clientError.value = toMessage(error, 'Unable to sign in with Apple.')
  } finally {
    socialAuthenticating.value = null
  }
}
</script>

<template>
  <section class="min-h-screen bg-[#f8f3ed]">
    <div class="grid min-h-screen items-stretch overflow-hidden lg:grid-cols-[1.05fr_0.95fr]">
      <div class="relative flex items-center justify-center bg-[#fffdfa] px-24 py-48 sm:px-40 lg:px-64 shadow-[10px_0_30px_rgba(79,59,67,0.08)] z-10">
        
        <form
          class="w-full max-w-[420px] space-y-32"
          @submit.prevent="attemptLogin"
        >
          <header class="space-y-12">
            <div class="flex items-center gap-3">
              <div class="flex h-10 w-10 items-center justify-center rounded-xl border border-white/70 bg-gradient-to-br from-rose-300/90 via-rose-200/70 to-emerald-200/70 text-[10px] font-semibold tracking-[0.18em] text-rose-900 shadow-[0_10px_28px_-16px_rgba(79,59,67,0.28)]">
                DS
              </div>
              <p class="text-12 font-600 uppercase tracking-[0.35em] text-[#b97180]">DragonShield</p>
            </div>
            <div>
              <h1 class="text-28 font-500 text-[#1f252d]">Secure Gateway</h1>
              <p class="mt-8 text-16 text-[#6c737f]">
                Sign in to manage code generation, routing, and product authenticity.
              </p>
            </div>
          </header>

          <AppAlert
            v-if="clientError"
            type="error"
            :message="clientError"
            show
            @dismiss-alert="clientError = null"
          />

          <div class="space-y-20">
            <div class="space-y-8">
              <FormInput
                v-model="credentials.email"
                label="Email"
                type="email"
                placeholder="administrator@example.com"
                autocomplete="email"
                :error="formError.email"
                required
              />
            </div>

            <div class="space-y-8">
              <FormInput
                v-model="credentials.password"
                label="Password"
                type="password"
                placeholder="********"
                autocomplete="current-password"
                :error="formError.password"
                required
              />
            </div>
          </div>

          <div class="space-y-16">
            <!-- Styling AppButton requires overriding if we want custom colors, but since it's a component from duanaga-ui, we use its props or wrap it -->
            <AppButton
              type="submit"
              color="black"
              block
              :loading="authenticating"
              text="Authenticate"
              style="background-color: #1f252d; border-radius: 0.8rem;"
            />

            <div class="grid gap-12 sm:grid-cols-2">
              <button
                type="button"
                class="rounded-12 border border-[#cfa0ab]/40 px-16 py-12 text-14 font-500 text-[#1f252d] transition hover:bg-[#cfa0ab]/10 hover:border-[#b97180]/50 disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="Boolean(socialAuthenticating)"
                @click="attemptGoogleLogin"
              >
                {{ socialAuthenticating === 'google' ? 'Authenticating...' : 'Sign in with Google' }}
              </button>
              <button
                type="button"
                class="rounded-12 border border-[#cfa0ab]/40 px-16 py-12 text-14 font-500 text-[#1f252d] transition hover:bg-[#cfa0ab]/10 hover:border-[#b97180]/50 disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="Boolean(socialAuthenticating)"
                @click="attemptAppleLogin"
              >
                {{ socialAuthenticating === 'apple' ? 'Authenticating...' : 'Sign in with Apple' }}
              </button>
            </div>

            <p class="text-14 text-[#6c737f]">
              Requires access?
              <RouterLink
                class="font-600 text-[#b97180] underline decoration-[#cfa0ab]/40 hover:decoration-[#b97180]"
                :to="{ name: 'register' }"
              >
                Request an account
              </RouterLink>
            </p>

            <p class="text-14 text-[#6c737f]">
              System offline? Email
              <a
                class="font-500 text-[#b97180] underline decoration-[#cfa0ab]/40 hover:decoration-[#b97180]"
                href="mailto:support@duanaga.app"
              >
                security@duanaga.app
              </a>
              for emergency access.
            </p>
          </div>
        </form>
      </div>

      <aside
        class="relative flex items-center bg-[#151318] px-32 py-48 text-[#f7f2f0] sm:px-48 lg:px-64"
      >
        <div class="absolute inset-0 opacity-80 mix-blend-screen">
          <div
            class="absolute inset-y-0 left-1/4 w-[600px] rotate-12 rounded-full bg-[#dea7b2]/10 blur-[100px]"
          ></div>
          <div
            class="absolute inset-y-0 right-[-20%] w-[460px] -rotate-12 rounded-full bg-[#96b8ad]/15 blur-[120px]"
          ></div>
          <!-- Fine grid overlay for technical/guard feel -->
          <div class="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:linear-gradient(to_bottom,black_40%,transparent)]"></div>
        </div>
        
        <div class="relative z-10 flex flex-col gap-40 w-full max-w-xl">
          <div class="space-y-16">
            <p class="text-12 font-600 uppercase tracking-[0.35em] text-[#dea7b2]">
              Security Protocol
            </p>
            <h2 class="leading-tight text-36 font-500 text-white">Zero-trust authenticity</h2>
            <p class="text-16 leading-relaxed text-[#b7bcc4]">
              The DragonShield platform enforces strict cryptographic standards for every generated 
              code batch. Unauthorized access is logged, and suspicious scans are 
              automatically flagged for review.
            </p>
          </div>

          <ul class="space-y-16">
            <li
              v-for="feature in featureHighlights"
              :key="feature.title"
              class="rounded-xl border border-white/5 bg-white/[0.02] p-24 backdrop-blur-md shadow-[0_14px_28px_-12px_rgba(0,0,0,0.85)] hover:bg-white/[0.04] hover:border-white/10 transition-colors duration-300"
            >
              <p class="flex items-center gap-3 text-12 font-600 uppercase tracking-[0.25em] text-[#c895a1]">
                <span class="h-1.5 w-1.5 rounded-full bg-[#96b8ad] shadow-[0_0_8px_rgba(150,184,173,0.8)]"></span>
                {{ feature.title }}
              </p>
              <p class="mt-8 text-16 leading-relaxed text-[#b7bcc4]/90">{{ feature.description }}</p>
            </li>
          </ul>

          <div class="mt-8 rounded-2xl border border-[#dea7b2]/20 bg-[#dea7b2]/5 p-24 backdrop-blur-lg">
            <p class="text-12 font-600 uppercase tracking-[0.35em] text-[#c895a1]">
              System Status
            </p>
            <div class="mt-12 flex items-center gap-4">
              <span class="relative flex h-3 w-3">
                <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#96b8ad] opacity-75"></span>
                <span class="relative inline-flex rounded-full h-3 w-3 bg-[#96b8ad]"></span>
              </span>
              <p class="text-20 font-500 text-white">Central Generator Online</p>
            </div>
            <p class="mt-6 text-14 text-[#b7bcc4]">
              Encrypted connection established. Awaiting authentication credentials.
            </p>
          </div>
        </div>
      </aside>
    </div>
  </section>
</template>
